import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { useTranslation } from "react-i18next";
import {
  ShoppingCart,
  Info,
  CirclePlay,
  BookOpen,
  Star,
  MoreHorizontal,
  Glasses,
  Clock,
  Zap,
  Layers,
  Wrench,
  GraduationCap,
  Sparkles,
  Waypoints,
  LayoutList,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { currencyAmount } from "../../lib/currency";
import { useNavigate } from "react-router-dom";
import { useState, useMemo } from "react";
import useAuth from "../../hooks/useAuth";
import useRetryFetch from "../../hooks/useRetryFetch";
import { config } from "../../Environment";
import { cn } from "../../lib/utils";
import RecommendModal from "../portal/modals/recommendModal/RecommendModal";
import MarketplaceExamplesModal from "./MarketplaceExamplesModal";

interface ProductCardProps {
  product: any;
  onTryNow?: () => void;
  isMobile?: boolean;
  variant?: "default" | "marketing";
  buttonLayout?: "horizontal" | "vertical";
}

// Map product types to icon + label + colors — all in teal/emerald family
const PRODUCT_TYPE_META: Record<string, { icon: React.ElementType; label: string; gradient: string; badge: string }> = {
  activity:   { icon: GraduationCap, label: "Activity",  gradient: "from-emerald-900 to-white",   badge: "bg-black/25 text-white backdrop-blur-sm" },
  service:    { icon: Wrench,        label: "Service",   gradient: "from-emerald-900 to-white",      badge: "bg-black/25 text-white backdrop-blur-sm" },
  bundle:     { icon: Layers,        label: "Bundle",    gradient: "from-emerald-900 to-white",   badge: "bg-black/25 text-white backdrop-blur-sm" },
  platform:   { icon: Zap,           label: "Platform",  gradient: "from-emerald-900 to-white",   badge: "bg-black/25 text-white backdrop-blur-sm" },
  pwf:        { icon: Waypoints,     label: "PWF",       gradient: "from-emerald-900 to-white",   badge: "bg-black/25 text-white backdrop-blur-sm" },
  program:    { icon: LayoutList,    label: "Program",   gradient: "from-emerald-900 to-white",   badge: "bg-black/25 text-white backdrop-blur-sm" },
};

const ProductCard = ({ product, onTryNow, isMobile, variant = "default", buttonLayout = "horizontal" }: ProductCardProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const retryFetch = useRetryFetch();
  const [showRecommendModal, setShowRecommendModal] = useState(false);
  const [showExamplesModal, setShowExamplesModal] = useState(false);

  const isActivityProduct = product.type === "activity";
  const isServiceProduct = product.type === "service";
  const allowsTrial = isActivityProduct && product.data?.activity?.allowTrial;

  const workflowId = useMemo(() =>
    product.data?.activity?.fiWorkflow,
    [product._id]
  );

  const typeMeta = PRODUCT_TYPE_META[product.type] || PRODUCT_TYPE_META.activity;
  const TypeIcon = typeMeta.icon;
  const isMarketing = variant === "marketing";

  const handleNavigateToProduct = () => {
    if (showExamplesModal) return;
    navigate(`/marketplace/product/${product.entityId}`);
  };

  const handleBuyClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/marketplace/product/${product.entityId}`);
  };

  const handleVendorClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (product.fiVendor?._id || product.fiVendor) {
      const vendorId = product.fiVendor?._id || product.fiVendor;
      navigate(`/marketplace/vendor/${vendorId}`);
    }
  };

  const handleViewExamples = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowExamplesModal(true);
  };

  const handleExploreService = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!auth?.email) {
      navigate("/login");
      return;
    }

    if (!product?.data?.service?.fiWorkflow) return;

    try {
      const processResponse = await retryFetch(
        `${config.API_BASE}/api/processes/${product.data.service.fiWorkflow}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ email: auth.email }),
        }
      );

      if (!processResponse.ok) throw new Error("Failed to create process");

      const processResult = await processResponse.json();
      const processId = processResult.processId;

      try {
        await retryFetch(`${config.API_BASE}/api/service-engagement`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ productId: product.entityId, processId }),
        });
      } catch (err) {
        console.error("[ProductCard] Failed to create service engagement:", err);
      }

      sessionStorage.setItem("prescriptionID", product.data.service.fiWorkflow);
      sessionStorage.setItem("processID", processId);
      sessionStorage.removeItem("nextActivity");

      const query = `wid=${product.data.service.fiWorkflow}&pid=${processId}`;
      setTimeout(() => navigate(`/activities/run?${query}`), 100);
    } catch (error) {
      console.error("Failed to start service:", error);
    }
  };

  const getLocalizedName = (name: any): string => {
    if (typeof name === "string") return name;
    if (typeof name === "object" && name !== null) {
      const lang = i18n.language || "en";
      if (lang === "he" && name.he) return name.he;
      if (lang === "de" && name.de) return name.de;
      return name.en || name.he || name.de || "";
    }
    return "";
  };

  const getContrastTextColor = (hexColor: string): string => {
    const hex = hexColor.replace("#", "");
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? "#000000" : "#FFFFFF";
  };

  const isFree = product.price?.amount === 0;
  const priceDisplay = isFree
    ? t("free") || "Free"
    : currencyAmount(product.price?.amount, product.price?.currency);

  return (
    <div
      className={cn(
        "group relative flex flex-col h-full cursor-pointer overflow-hidden",
        "rounded-2xl bg-white",
        "border border-slate-200/70",
        "shadow-[0_1px_3px_rgba(0,0,0,0.04),0_4px_12px_rgba(0,0,0,0.03)]",
        "hover:shadow-[0_4px_20px_rgba(0,0,0,0.08),0_1px_4px_rgba(0,0,0,0.04)]",
        "hover:-translate-y-1 transition-all duration-300 ease-out",
        product.isFeatured && "border-primary/25 shadow-[0_2px_12px_rgba(37,99,235,0.08)]"
      )}
      onClick={handleNavigateToProduct}
    >
      {/* ── Image / Hero area ─────────────────────────────────────────── */}
      <div className={cn(
        "relative overflow-hidden shrink-0",
        isMarketing ? "h-40" : "h-40",
        !product.imageUrl && `bg-gradient-to-br ${typeMeta.gradient}`
      )}>
        {product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt={product.name}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
            onError={(e) => {
              (e.target as HTMLImageElement).parentElement!.classList.add(
                "bg-gradient-to-br", ...typeMeta.gradient.split(" ")
              );
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <>
            <div className="absolute inset-0 opacity-[0.07]"
              style={{ backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)", backgroundSize: "20px 20px" }} />
            <div className="absolute inset-0 flex items-center justify-center">
              <TypeIcon className="w-14 h-14 text-white/40" />
            </div>
          </>
        )}

        {/* Subtle overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />

        {/* Type badge — top left (consistent glass style on any image) */}
        <div className="absolute top-2.5 start-2.5 z-10">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold text-white backdrop-blur-sm border border-white/20"
            style={{ background: "rgba(0,0,0,0.28)" }}>
            <TypeIcon className="w-2.5 h-2.5" />
            {t(product.type) || typeMeta.label}
          </span>
        </div>

        {/* Featured badge — top right */}
        {product.isFeatured && (
          <div className="absolute top-2.5 end-2.5 z-10">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-400/95 text-amber-900 backdrop-blur-sm">
              <Star className="w-2.5 h-2.5 fill-current" />
              {t("featured") || "Featured"}
            </span>
          </div>
        )}

        {/* Price pill — bottom right */}
        <div className="absolute bottom-2.5 end-2.5 z-10">
          <span className={cn(
            "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold shadow backdrop-blur-sm",
            isFree ? "bg-emerald-500/95 text-white" : "bg-white/95 text-gray-900"
          )}>
            {priceDisplay}
          </span>
        </div>

        {/* Title overlay for marketing variant */}
        {isMarketing && (
          <div className="absolute bottom-0 start-0 end-0 px-4 pb-3 pt-8 z-10">
            <h3 className="text-left text-lg font-bold text-white drop-shadow-lg line-clamp-2"
              style={{ textShadow: "0 2px 8px rgba(0,0,0,0.8)" }}>
              {product.name}
            </h3>
          </div>
        )}
      </div>

      {/* ── Card body ─────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col px-4 pt-3 pb-0">
        {/* Title row (non-marketing) */}
        {!isMarketing && (
          <div className="flex items-start justify-between gap-2 mb-1.5">
            <h3 className="text-sm font-semibold text-gray-900 group-hover:text-primary transition-colors line-clamp-2 leading-snug flex-1">
              {product.name}
            </h3>
            {/* Three-dot dropdown */}
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger
                className="shrink-0 -me-1 -mt-0.5 p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {isActivityProduct && (product.publishedExampleCount ?? 0) > 0 && (
                  <DropdownMenuItem className="cursor-pointer" onClick={(e) => { e.stopPropagation(); handleViewExamples(e); }}>
                    <BookOpen className="me-2 h-4 w-4" />
                    <span>{t("view_examples") || "View Examples"}</span>
                    <Badge variant="secondary" className="ms-2 h-4 px-1.5 text-xs bg-purple-100 text-purple-700">
                      {product.publishedExampleCount}
                    </Badge>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem className="cursor-pointer" onClick={(e) => { e.stopPropagation(); setShowRecommendModal(true); }}>
                  <Star className="me-2 h-4 w-4" />
                  <span>{t("recommend_activity")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        {/* Vendor row */}
        {product.vendorName && (
          <button
            onClick={handleVendorClick}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-primary transition-colors mb-2 w-fit"
          >
            {product.fiVendor?.logoURL ? (
              <img src={product.fiVendor.logoURL} alt="" className="w-4 h-4 rounded-full object-cover shrink-0" style={{ boxShadow: "0 0 0 1px rgba(0,0,0,0.08)" }} />
            ) : (
              <span className="w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center shrink-0" style={{ background: "hsl(var(--primary) / 0.12)", color: "hsl(var(--primary))" }}>
                {product.vendorName.charAt(0).toUpperCase()}
              </span>
            )}
            <span className="truncate max-w-[140px]">{product.vendorName}</span>
          </button>
        )}

        {/* Categories & tags */}
        {!isMarketing && (
          <div className="flex flex-wrap gap-1 mb-2">
            {product.categories?.slice(0, 2).map((category: any, index: number) => {
              const isObj = typeof category === "object" && category._id;
              const name = isObj ? getLocalizedName(category.name) : category;
              const bg = isObj ? category.color : "#6B7280";
              const color = isObj ? (category.textColor || getContrastTextColor(category.color)) : "#FFFFFF";
              return (
                <span
                  key={`cat-${isObj ? category._id : index}`}
                  className="text-[10px] font-medium px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: bg, color }}
                >
                  {name}
                </span>
              );
            })}
            {product.categories?.length > 2 && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                +{product.categories.length - 2}
              </span>
            )}
            {product.tags?.slice(0, 2).map((tag: any, index: number) => {
              const isObj = typeof tag === "object" && tag._id;
              const name = isObj ? getLocalizedName(tag.name) : tag;
              const bg = isObj ? tag.color : "#F3F4F6";
              const color = isObj ? (tag.textColor || getContrastTextColor(tag.color)) : "#4B5563";
              return (
                <span
                  key={`tag-${isObj ? tag._id : index}`}
                  className="text-[10px] px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: bg, color }}
                >
                  {name}
                </span>
              );
            })}
          </div>
        )}

        {/* Metadata badges row */}
        {!isMarketing && (isActivityProduct || product.freeCredits?.enabled) && (
          <div className="flex flex-wrap gap-1 mb-2">
            {isActivityProduct && product.data?.activity?.difficulty && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200/60 capitalize">
                {t(product.data.activity.difficulty) || product.data.activity.difficulty}
              </span>
            )}
            {isActivityProduct && product.data?.activity?.estimatedTime && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200/60 inline-flex items-center gap-0.5">
                <Clock className="w-2.5 h-2.5" />
                {product.data.activity.estimatedTime} {t("min") || "min"}
              </span>
            )}
            {isActivityProduct && product.data?.activity?.usageLimit && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/5 text-primary border border-primary/15">
                {product.data.activity.usageLimit}× {product.data.activity.usageLimit === 1 ? t("use") || "use" : t("uses") || "uses"}
              </span>
            )}
            {isActivityProduct && product.data?.activity?.expirationDays && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200/60">
                {product.data.activity.expirationDays}d
              </span>
            )}
            {product.freeCredits?.enabled && product.freeCredits?.amount > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200/60">
                +{product.freeCredits.amount} {t("mentor_session_credit") || "Credits"}
              </span>
            )}
          </div>
        )}

        {/* Description */}
        {product.description && (
          <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed mb-2 flex-1">
            {product.description}
          </p>
        )}

        {/* AI search explanation — why this result matches the query */}
        {product.aiExplanation && (
          <div className="flex items-start gap-1.5 px-2 py-1.5 mb-2 rounded-lg bg-violet-50/80 border border-violet-100">
            <Sparkles className="w-3 h-3 mt-0.5 shrink-0 text-violet-500" />
            <p className="text-[11px] text-violet-700 leading-snug line-clamp-2">
              {product.aiExplanation}
            </p>
          </div>
        )}

        {/* Price — marketing variant only */}
        {isMarketing && (
          <div className="text-sm font-bold text-gray-900 mt-1 mb-1">
            {priceDisplay}
          </div>
        )}
      </div>

      {/* ── Footer / Actions ──────────────────────────────────────────── */}
      <div className="px-4 py-3 mt-auto border-t border-gray-100/80" style={{ background: "rgba(248,250,252,0.7)" }}>
        {buttonLayout === "vertical" ? (
          <div className="flex flex-col gap-1.5">
            {isServiceProduct ? (
              <>
                <Button size="sm" onClick={handleExploreService}
                  className="w-full bg-primary hover:bg-primary/90 text-white shadow-sm shadow-primary/20">
                  <CirclePlay className="w-3.5 h-3.5 me-1.5" />
                  {t("action_explore", "Explore")}
                </Button>
                <Button size="sm" variant="outline" onClick={handleBuyClick} className="w-full hover:bg-gray-50">
                  <Info className="w-3.5 h-3.5 me-1.5" />
                  {t("info") || "Info"}
                </Button>
              </>
            ) : allowsTrial && onTryNow ? (
              <>
                <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onTryNow(); }}
                  className="w-full hover:bg-gray-50">
                  <Glasses className="w-3.5 h-3.5 me-1.5" />
                  {t("action_try_free", "Try Free")}
                </Button>
                <Button size="sm" onClick={handleBuyClick}
                  className="w-full bg-primary hover:bg-primary/90 text-white shadow-sm shadow-primary/20">
                  <ShoppingCart className="w-3.5 h-3.5 me-1.5" />
                  {t("action_get_started", "Get Started")}
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={handleBuyClick}
                className="w-full bg-primary hover:bg-primary/90 text-white shadow-sm shadow-primary/20">
                <ShoppingCart className="w-3.5 h-3.5 me-1.5" />
                {t("action_get_started", "Get Started")}
              </Button>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {/* Examples icon button */}
            {isActivityProduct && workflowId && (product.publishedExampleCount ?? 0) > 0 && (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="sm" variant="outline" onClick={handleViewExamples}
                      className="shrink-0 px-2 hover:bg-purple-50 hover:text-purple-700 hover:border-purple-300">
                      <BookOpen className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t("view_examples") || "View Examples"} ({product.publishedExampleCount})</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {isServiceProduct ? (
              <>
                <Button size="sm" onClick={handleExploreService}
                  className="flex-1 bg-primary hover:bg-primary/90 text-white shadow-sm shadow-primary/20">
                  <CirclePlay className="w-3.5 h-3.5 me-1.5" />
                  {t("action_explore", "Explore")}
                </Button>
                <Button size="sm" variant="outline" onClick={handleBuyClick} className="flex-1 hover:bg-gray-50">
                  <Info className="w-3.5 h-3.5 me-1.5" />
                  {t("info") || "Info"}
                </Button>
              </>
            ) : allowsTrial && onTryNow ? (
              <>
                <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onTryNow(); }}
                  className="flex-1 hover:bg-gray-50">
                  <Glasses className="w-3.5 h-3.5 me-1.5" />
                  {t("action_try_free", "Try Free")}
                </Button>
                <Button size="sm" onClick={handleBuyClick}
                  className="flex-1 bg-primary hover:bg-primary/90 text-white shadow-sm shadow-primary/20">
                  <ShoppingCart className="w-3.5 h-3.5 me-1.5" />
                  {t("action_get_started", "Get Started")}
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={handleBuyClick}
                className="flex-1 bg-primary hover:bg-primary/90 text-white shadow-sm shadow-primary/20">
                <ShoppingCart className="w-3.5 h-3.5 me-1.5" />
                {t("action_get_started", "Get Started")}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {showRecommendModal && isActivityProduct && workflowId && (
        <RecommendModal
          open={showRecommendModal}
          onClose={() => setShowRecommendModal(false)}
          workflowId={workflowId}
          workflowName={product.name}
        />
      )}
      {showExamplesModal && isActivityProduct && workflowId && (
        <MarketplaceExamplesModal
          open={showExamplesModal}
          onOpenChange={setShowExamplesModal}
          workflowId={workflowId}
          activityName={product.name}
        />
      )}
    </div>
  );
};

export default ProductCard;
